import React, { useState } from 'react';
import { Button } from './button';
import { Dialog, DialogTitle, DialogBody } from './dialog';
import { ClockIcon } from '@heroicons/react/24/outline';
import { db } from '../firebase';
import { logBitacora } from '../utils/bitacora';
import { mutateFianza } from '../utils/contractWrites';
import {
  ENDOSO_TIPOS,
  EVENTO_TIPO_LABELS,
  ENDOSO_SOLICITANTES,
  ENDOSO_SOLICITANTE_LABELS,
  buildEndosoPendiente,
  getEndososPendientes,
} from '../utils/fianzaEventos';

const msToDateInput = (ms) => {
  if (!ms) return '';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const dateInputToMs = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const t = new Date(y, m - 1, d).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * SolicitarEndosoDialog — records that an endoso has been REQUESTED but not yet
 * applied, so Pulso can remind the agente instead of the request living in an
 * inbox somewhere.
 *
 * This is the forward-looking counterpart to EndosoDialog: eventos[] only ever
 * records what already happened.
 */
export function SolicitarEndosoDialog({
  isOpen, onClose, fianza, user, userData, buildBitacoraMeta, onApplied,
}) {
  const [tipos, setTipos] = useState([]);
  const [descripcion, setDescripcion] = useState('');
  const [solicitadoPor, setSolicitadoPor] = useState('cliente');
  const [fechaLimite, setFechaLimite] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setTipos([]);
    setDescripcion('');
    setSolicitadoPor('cliente');
    setFechaLimite('');
    setError(null);
    onClose();
  };

  const toggleTipo = (t) =>
    setTipos((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const handleSubmit = async () => {
    if (!tipos.length && !descripcion.trim()) {
      setError('Indica qué endoso se necesita: marca un tipo o descríbelo.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ts = Date.now();
      const pendiente = buildEndosoPendiente({
        tipos,
        descripcion,
        solicitadoPor,
        fechaLimite: dateInputToMs(fechaLimite),
        createdBy: { uid: user?.uid || null, nombre: userData?.nombre || userData?.correo || null },
        ts,
      });

      await mutateFianza(db, fianza.contratoId, fianza.id, (current) => ({
        ...current,
        endososPendientes: [...getEndososPendientes(current), pendiente],
        updatedAt: ts,
      }));

      try {
        const label = tipos.map((t) => EVENTO_TIPO_LABELS[t] || t).join(' + ') || 'sin tipo';
        const numero = fianza.numeroFianza || 'sin número';
        const desc = `Registró endoso pendiente (${label}) en fianza #${numero}`;
        await logBitacora({
          action: 'UPDATE',
          collection: 'contracts',
          documentId: fianza.contratoId,
          user,
          userData,
          metadata: buildBitacoraMeta
            ? buildBitacoraMeta(desc, { endosoPendienteId: pendiente.id, tipos })
            : { contratoId: fianza.contratoId, fianzaId: fianza.id, description: desc },
        });
      } catch (e) {
        console.error('bitacora endoso pendiente failed', e);
      }

      onApplied?.();
      resetAndClose();
    } catch (e) {
      console.error('solicitar endoso failed', e);
      setError(e.message || 'No se pudo registrar el endoso pendiente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={submitting ? () => {} : resetAndClose} size="xl">
      <DialogTitle><span className="text-azul-surelia">Solicitar endoso</span></DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          <div className="rounded-lg bg-cian-digital/10 border border-cian-digital/30 px-3 py-2 text-xs text-azul-surelia">
            Registra un endoso que te pidieron y que todavía no aplicas. Aparecerá en
            Pulso hasta que lo registres, para que no se te pase.
          </div>

          <div>
            <div className="text-sm font-semibold text-gray-800 mb-1">¿Qué cambia?</div>
            <p className="text-[11px] text-gray-500 mb-2">
              Opcional si lo describes abajo. Puedes marcar varios.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {ENDOSO_TIPOS.map((t) => (
                <label
                  key={t}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors ${
                    tipos.includes(t)
                      ? 'border-azul-surelia bg-azul-surelia/5 text-azul-surelia'
                      : 'border-gray-300 text-gray-700 hover:border-azul-surelia/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={tipos.includes(t)}
                    onChange={() => toggleTipo(t)}
                    className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                  />
                  {EVENTO_TIPO_LABELS[t]}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Descripción de lo que te pidieron
            </label>
            <textarea
              rows={3}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej. El beneficiario pide ampliar la vigencia hasta el cierre de obra."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">¿Quién lo pidió?</label>
              <select
                value={solicitadoPor}
                onChange={(e) => setSolicitadoPor(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia"
              >
                {ENDOSO_SOLICITANTES.map((s) => (
                  <option key={s} value={s}>{ENDOSO_SOLICITANTE_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha límite (opcional)
              </label>
              <input
                type="date"
                value={fechaLimite}
                onChange={(e) => setFechaLimite(e.target.value)}
                min={msToDateInput(Date.now())}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Sin fecha aparece igual en Pulso, pero sin urgencia.
              </p>
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={resetAndClose} disabled={submitting}>Cancelar</Button>
            <Button color="surelia" onClick={handleSubmit} disabled={submitting}>
              <ClockIcon className="w-4 h-4" />
              {submitting ? 'Registrando…' : 'Registrar pendiente'}
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default SolicitarEndosoDialog;
